import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getUserSessionScopeSuffix } from '@/utils/userSessionScope';

export type AiTaskKind =
  | 'chat'
  | 'document_deep_review'
  | 'document_preliminary_review'
  | 'component_recommend'
  | 'component_bom_import'
  | 'component_autofill'
  | 'bom_generate'
  | 'bom_cost_estimate'
  | 'drawing_review'
  | 'tr_review'
  | 'project_intro_rewrite'
  | 'project_create'
  | 'calculator'
  | 'sor_requirement_analyze'
  | 'wbs_generate'
  | 'wbs_task_reassign'
  | 'other';

export type AiTaskStatus = 'queued' | 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';

export type AiTaskLifecycle = {
  onPause?: () => void;
  onResume?: () => void;
  /** 回退/停止时丢弃已应用到项目的结果 */
  onRollback?: () => void | Promise<void>;
  /** 停止时额外清理（可选；默认走 onRollback） */
  onStop?: () => void | Promise<void>;
};

export interface AiQueuedTaskMeta {
  id: string;
  scopeKey: string;
  sessionId: string;
  kind: AiTaskKind;
  label: string;
  status: AiTaskStatus;
  createdAt: number;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
}

export interface AiTaskRunContext {
  scopeKey: string;
  sessionId: string;
  taskId: string;
  signal: AbortSignal;
}

type TaskExecutor = (ctx: AiTaskRunContext) => Promise<void>;

type TaskPromiseHandlers<T> = {
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
};

const QUEUE_STORAGE_PREFIX = 'aipd_ai_task_queue_';

export class TaskCancelledError extends Error {
  constructor(message = '已取消') {
    super(message);
    this.name = 'TaskCancelledError';
  }
}

export function isTaskCancelledError(error: unknown): boolean {
  if (error instanceof TaskCancelledError) return true;
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error && typeof error === 'object' && 'name' in error) {
    const name = String((error as { name?: unknown }).name ?? '');
    if (name === 'TaskCancelledError' || name === 'AbortError') return true;
  }
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return msg.includes('已取消') || msg.toLowerCase().includes('aborted');
}

function queueStorageKey(): string {
  return `${QUEUE_STORAGE_PREFIX}${getUserSessionScopeSuffix()}`;
}

function genTaskId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return `ait-${g.crypto.randomUUID()}`;
  return `ait-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function loadPersistedTasks(): AiQueuedTaskMeta[] {
  try {
    const raw = localStorage.getItem(queueStorageKey());
    if (!raw) return [];
    const arr = JSON.parse(raw) as AiQueuedTaskMeta[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

  function savePersistedTasks(tasks: AiQueuedTaskMeta[]) {
  try {
    const active = tasks.filter((t) => t.status === 'queued' || t.status === 'running' || t.status === 'paused');
    localStorage.setItem(queueStorageKey(), JSON.stringify(active));
  } catch {
    /* ignore */
  }
}

export const useAiTaskQueueStore = defineStore('aiTaskQueue', () => {
  const tasks = ref<AiQueuedTaskMeta[]>(loadPersistedTasks());
  const executors = new Map<string, TaskExecutor>();
  const abortControllers = new Map<string, AbortController>();
  const taskPromises = new Map<string, TaskPromiseHandlers<unknown>>();
  const taskLifecycles = new Map<string, AiTaskLifecycle>();
  const processingScopes = new Set<string>();
  const scopeQueueHeld = new Set<string>();
  /** 终止当前任务后暂缓的排队任务：新发送可执行，这些任务需点「继续」才恢复 */
  const deferredQueuedTaskIds = new Set<string>();

  /** 页面刷新后无法恢复的执行器；SOR 解析由项目页 localStorage + 全局轮询恢复 */
  function recoverAfterReload() {
    let changed = false;
    const kept: typeof tasks.value = [];
    for (const t of tasks.value) {
      if (
        t.kind === 'sor_requirement_analyze'
        && (t.status === 'queued' || t.status === 'running')
      ) {
        changed = true;
        continue;
      }
      kept.push(t);
    }
    if (changed) {
      tasks.value = kept;
      savePersistedTasks(tasks.value);
    }
    for (const t of tasks.value) {
      if (t.status === 'queued' || t.status === 'running' || t.status === 'paused') {
        t.status = 'failed';
        t.finishedAt = Date.now();
        t.error = '页面已刷新，任务已中断，请重新发起';
        changed = true;
      }
    }
    if (changed) {
      tasks.value = [...tasks.value];
      savePersistedTasks(tasks.value);
    }
  }

  recoverAfterReload();

  function persist() {
    savePersistedTasks(tasks.value);
  }

  function tasksForScope(scopeKey: string): AiQueuedTaskMeta[] {
    return tasks.value.filter((t) => t.scopeKey === scopeKey);
  }

  function queuedCountForScope(scopeKey: string): number {
    return tasksForScope(scopeKey).filter((t) => t.status === 'queued').length;
  }

  function runningTaskForScope(scopeKey: string): AiQueuedTaskMeta | null {
    return tasksForScope(scopeKey).find((t) => t.status === 'running') ?? null;
  }

  function isScopeBusy(scopeKey: string): boolean {
    return tasksForScope(scopeKey).some(
      (t) => t.status === 'queued' || t.status === 'running' || t.status === 'paused',
    );
  }

  function isSessionActive(sessionId: string): boolean {
    return tasks.value.some(
      (t) => t.sessionId === sessionId && (t.status === 'queued' || t.status === 'running' || t.status === 'paused'),
    );
  }

  function sessionHasQueuedOrRunning(scopeKey: string, sessionId: string): boolean {
    return tasks.value.some(
      (t) =>
        t.scopeKey === scopeKey
        && t.sessionId === sessionId
        && (t.status === 'queued' || t.status === 'running' || t.status === 'paused'),
    );
  }

  function pausedTaskForScope(scopeKey: string): AiQueuedTaskMeta | null {
    return tasksForScope(scopeKey).find((t) => t.status === 'paused') ?? null;
  }

  function hasPausedInScope(scopeKey: string): boolean {
    return pausedTaskForScope(scopeKey) != null;
  }

  function isScopeQueueHeld(scopeKey: string): boolean {
    if (scopeQueueHeld.has(scopeKey)) return true;
    return tasksForScope(scopeKey).some(
      (t) => t.status === 'queued' && deferredQueuedTaskIds.has(t.id),
    );
  }

  function isTaskQueueDeferred(taskId: string): boolean {
    return deferredQueuedTaskIds.has(taskId);
  }

  function cleanupTaskRuntime(taskId: string) {
    executors.delete(taskId);
    abortControllers.delete(taskId);
    taskPromises.delete(taskId);
    taskLifecycles.delete(taskId);
    deferredQueuedTaskIds.delete(taskId);
  }

  function cancelTask(taskId: string): boolean {
    const task = tasks.value.find((t) => t.id === taskId);
    if (!task || (task.status !== 'queued' && task.status !== 'running' && task.status !== 'paused')) {
      return false;
    }
    const controller = abortControllers.get(taskId);
    controller?.abort();
    executors.delete(taskId);
    task.status = 'cancelled';
    task.finishedAt = Date.now();
    task.error = '已取消';
    tasks.value = [...tasks.value];
    persist();
    const handlers = taskPromises.get(taskId);
    if (handlers) {
      handlers.reject(new TaskCancelledError());
      taskPromises.delete(taskId);
    }
    cleanupTaskRuntime(taskId);
    return true;
  }

  function cancelRunningForScope(scopeKey: string): boolean {
    const running = runningTaskForScope(scopeKey);
    if (running) return cancelTask(running.id);
    const paused = pausedTaskForScope(scopeKey);
    return paused ? cancelTask(paused.id) : false;
  }

  function pauseTask(taskId: string): boolean {
    const task = tasks.value.find((t) => t.id === taskId);
    if (!task || task.status !== 'running') return false;
    taskLifecycles.get(taskId)?.onPause?.();
    task.status = 'paused';
    task.finishedAt = undefined;
    tasks.value = [...tasks.value];
    persist();
    return true;
  }

  function resumeTask(taskId: string): boolean {
    const task = tasks.value.find((t) => t.id === taskId);
    if (!task || task.status !== 'paused') return false;
    task.status = 'running';
    task.finishedAt = undefined;
    tasks.value = [...tasks.value];
    persist();
    taskLifecycles.get(taskId)?.onResume?.();
    return true;
  }

  async function rollbackTask(taskId: string): Promise<boolean> {
    const task = tasks.value.find((t) => t.id === taskId);
    if (!task || (task.status !== 'paused' && task.status !== 'running' && task.status !== 'queued')) {
      return false;
    }
    try {
      await taskLifecycles.get(taskId)?.onRollback?.();
    } catch {
      /* ignore rollback handler errors */
    }
    deferQueuedTasksInScope(task.scopeKey);
    scopeQueueHeld.add(task.scopeKey);
    return cancelTask(taskId);
  }

  /** 将 scope 内仍排队的任务标记为暂缓，避免前序任务异常结束后自动开跑 */
  function deferQueuedTasksInScope(scopeKey: string) {
    for (const t of tasksForScope(scopeKey)) {
      if (t.status === 'queued') {
        deferredQueuedTaskIds.add(t.id);
      }
    }
  }

  /**
   * 停止当前任务：仅中止执行并暂缓后续排队，不自动回退项目侧修改。
   * 用户再次发送时可选择是否回退。
   */
  async function stopTask(taskId: string): Promise<boolean> {
    const task = tasks.value.find((t) => t.id === taskId);
    if (!task || (task.status !== 'paused' && task.status !== 'running' && task.status !== 'queued')) {
      return false;
    }
    deferQueuedTasksInScope(task.scopeKey);
    scopeQueueHeld.add(task.scopeKey);
    try {
      await taskLifecycles.get(taskId)?.onStop?.();
    } catch {
      /* ignore stop handler errors */
    }
    return cancelTask(taskId);
  }

  function continueScopeQueue(scopeKey: string) {
    for (const t of tasksForScope(scopeKey)) {
      deferredQueuedTaskIds.delete(t.id);
    }
    scopeQueueHeld.delete(scopeKey);
    void processScope(scopeKey);
  }

  /** 新任务入队时解除 hold，使新任务可执行；已暂缓的排队任务仍需「继续」 */
  function releaseHoldForNewEnqueue(scopeKey: string) {
    scopeQueueHeld.delete(scopeKey);
  }

  async function processScope(scopeKey: string) {
    if (processingScopes.has(scopeKey)) return;
    if (hasPausedInScope(scopeKey) || scopeQueueHeld.has(scopeKey)) return;
    processingScopes.add(scopeKey);
    try {
      while (true) {
        if (hasPausedInScope(scopeKey) || scopeQueueHeld.has(scopeKey)) break;
        const next = tasksForScope(scopeKey).find(
          (t) => t.status === 'queued' && !deferredQueuedTaskIds.has(t.id),
        );
        if (!next) break;
        const executor = executors.get(next.id);
        const controller = abortControllers.get(next.id);
        if (!executor || !controller) {
          next.status = 'failed';
          next.finishedAt = Date.now();
          next.error = '任务执行器丢失，请重新发起';
          persist();
          cleanupTaskRuntime(next.id);
          continue;
        }
        if (controller.signal.aborted) {
          next.status = 'cancelled';
          next.finishedAt = Date.now();
          next.error = '已取消';
          persist();
          cleanupTaskRuntime(next.id);
          continue;
        }
        next.status = 'running';
        next.startedAt = Date.now();
        persist();
        try {
          await executor({
            scopeKey: next.scopeKey,
            sessionId: next.sessionId,
            taskId: next.id,
            signal: controller.signal,
          });
          if (controller.signal.aborted) {
            next.status = 'cancelled';
            next.error = '已取消';
          } else {
            next.status = 'completed';
          }
          next.finishedAt = Date.now();
        } catch (e: unknown) {
          if (isTaskCancelledError(e) || controller.signal.aborted) {
            next.status = 'cancelled';
            next.error = '已取消';
          } else {
            next.status = 'failed';
            next.error = e instanceof Error ? e.message : String(e ?? '任务失败');
          }
          next.finishedAt = Date.now();
        } finally {
          cleanupTaskRuntime(next.id);
          tasks.value = [...tasks.value];
          persist();
        }
      }
    } finally {
      processingScopes.delete(scopeKey);
    }
    if (
      tasksForScope(scopeKey).some(
        (t) => t.status === 'queued' && !deferredQueuedTaskIds.has(t.id),
      )
    ) {
      void processScope(scopeKey);
    }
  }

  /**
   * 将 AI 任务加入 scope 串行队列；若当前有任务执行中则排队等待。
   * 返回 Promise，在任务完成或失败时 resolve/reject。
   */
  function enqueue<T>(opts: {
    scopeKey: string;
    sessionId: string;
    kind: AiTaskKind;
    label: string;
    lifecycle?: AiTaskLifecycle;
    execute: (ctx: AiTaskRunContext) => Promise<T>;
  }): Promise<T> {
    const taskId = genTaskId();
    const controller = new AbortController();
    const meta: AiQueuedTaskMeta = {
      id: taskId,
      scopeKey: opts.scopeKey,
      sessionId: opts.sessionId,
      kind: opts.kind,
      label: opts.label,
      status: 'queued',
      createdAt: Date.now(),
    };
    tasks.value = [...tasks.value, meta];
    abortControllers.set(taskId, controller);
    if (opts.lifecycle) {
      taskLifecycles.set(taskId, opts.lifecycle);
    }
    persist();
    // 新发送的任务可立即调度；终止后暂缓的旧排队任务仍保持 deferred
    releaseHoldForNewEnqueue(opts.scopeKey);

    return new Promise<T>((resolve, reject) => {
      taskPromises.set(taskId, { resolve: resolve as (value: unknown) => void, reject });
      executors.set(taskId, async (ctx) => {
        try {
          const result = await opts.execute(ctx);
          if (controller.signal.aborted) {
            throw new TaskCancelledError();
          }
          resolve(result);
        } catch (e) {
          reject(e);
          throw e;
        }
      });
      void processScope(opts.scopeKey);
    });
  }

  function clearCompleted(scopeKey?: string) {
    tasks.value = tasks.value.filter((t) => {
      if (scopeKey && t.scopeKey !== scopeKey) return true;
      return t.status === 'queued' || t.status === 'running' || t.status === 'paused';
    });
    persist();
  }

  function resetForUserSwitch() {
    for (const controller of abortControllers.values()) {
      controller.abort();
    }
    tasks.value = [];
    executors.clear();
    abortControllers.clear();
    taskPromises.clear();
    taskLifecycles.clear();
    processingScopes.clear();
    scopeQueueHeld.clear();
    deferredQueuedTaskIds.clear();
    try {
      localStorage.removeItem(queueStorageKey());
    } catch {
      /* ignore */
    }
  }

  const globalQueuedCount = computed(
    () => tasks.value.filter((t) => t.status === 'queued').length,
  );

  function activeTasksForScope(scopeKey: string): AiQueuedTaskMeta[] {
    return tasksForScope(scopeKey)
      .filter((t) => t.status === 'queued' || t.status === 'running' || t.status === 'paused')
      .sort((a, b) => {
        const rank = (status: AiTaskStatus) => {
          if (status === 'running') return 0;
          if (status === 'paused') return 1;
          return 2;
        };
        const diff = rank(a.status) - rank(b.status);
        if (diff !== 0) return diff;
        return a.createdAt - b.createdAt;
      });
  }

  return {
    tasks,
    enqueue,
    cancelTask,
    cancelRunningForScope,
    pauseTask,
    resumeTask,
    rollbackTask,
    stopTask,
    continueScopeQueue,
    isTaskCancelledError,
    tasksForScope,
    activeTasksForScope,
    queuedCountForScope,
    runningTaskForScope,
    pausedTaskForScope,
    hasPausedInScope,
    isScopeQueueHeld,
    isTaskQueueDeferred,
    isScopeBusy,
    isSessionActive,
    sessionHasQueuedOrRunning,
    clearCompleted,
    resetForUserSwitch,
    globalQueuedCount,
  };
});
